import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import { useAppStore } from '../stores/useAppStore';
import { loanService } from '../services/loanService';
import type { Book } from '../types';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';

interface BookDetailProps {
  open: boolean;
  onClose: () => void;
  book: Book | null;
}

export const BookDetail: React.FC<BookDetailProps> = ({ open, onClose, book }) => {
  const { user } = useAppStore();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const [isBorrowing, setIsBorrowing] = useState(false);
  
  if (!book) return null;
  
  const handleBorrow = async () => {
    if (!user?.id) {
      showError('Vui lòng đăng nhập để mượn sách');
      return;
    }
    
    if (book.availableCopies <= 0) {
      showError('Sách này hiện không có sẵn');
      return;
    }
    
    setIsBorrowing(true);
    try {
      const result = await loanService.checkoutBooks(user.id, [book.id]);
      if (result.success) {
        showSuccess(result.message);
        // Refresh book data after borrowing
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1500);
      } else {
        showError(result.message || 'Có lỗi xảy ra khi mượn sách');
      }
    } catch (error: any) {
      showError(error.message || 'Có lỗi xảy ra khi mượn sách');
    } finally {
      setIsBorrowing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAvailabilityColor = (available: number, total: number) => {
    const percentage = (available / total) * 100;
    if (percentage === 0) return 'error';
    if (percentage < 30) return 'warning';
    return 'success';
  };

  const getAvailabilityText = (available: number, total: number) => {
    if (available === 0) return 'Hết sách';
    if (available < total * 0.3) return 'Sắp hết';
    return 'Có sẵn';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          {book.title}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" component="div">
          {book.author}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
          <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Thông tin cơ bản
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tác giả
                  </Typography>
                  <Typography variant="body1">{book.author}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Nhà xuất bản
                  </Typography>
                  <Typography variant="body1">
                    {book.publisher || 'Không có thông tin'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Năm xuất bản
                  </Typography>
                  <Typography variant="body1">
                    {book.publishYear || 'Không có thông tin'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    ISBN
                  </Typography>
                  <Typography variant="body1">
                    {book.isbn || 'Không có thông tin'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Danh mục
                  </Typography>
                  {book.category ? (
                    <Chip label={book.category.name} size="small" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Chưa phân loại
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Trạng thái
                  </Typography>
                  <Chip
                    label={getAvailabilityText(book.availableCopies, book.totalCopies)}
                    color={getAvailabilityColor(book.availableCopies, book.totalCopies)}
                    size="small"
                  />
                </Box>
              </Box>
            </Paper>

            {book.subjects && book.subjects.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Chủ đề
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {book.subjects.map((subject, index) => (
                    <Chip key={index} label={subject} size="small" />
                  ))}
                </Box>
              </Paper>
            )}

            {book.summary && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Tóm tắt
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {book.summary}
                </Typography>
              </Paper>
            )}

            {book.notes && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Ghi chú
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {book.notes}
                </Typography>
              </Paper>
            )}
          </Box>

          <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Thống kê
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tổng số bản sao
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {book.totalCopies}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Số bản sao có sẵn
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {book.availableCopies}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Số bản sao đã mượn
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {book.totalCopies - book.availableCopies}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Thông tin hệ thống
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Ngày tạo
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(book.dateCreated)}
                  </Typography>
                </Box>
                {book.dateUpdated && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Ngày cập nhật
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(book.dateUpdated)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleBorrow} 
          variant="contained" 
          color="primary"
          disabled={book.availableCopies <= 0 || isBorrowing}
          sx={{ mr: 1 }}
        >
          {isBorrowing ? 'Đang xử lý...' : book.availableCopies > 0 ? 'MƯỢN NHANH' : 'Hết sách'}
        </Button>
        <Button onClick={onClose} variant="outlined">
          Đóng
        </Button>
      </DialogActions>
      
      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Dialog>
  );
};
